import React from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ShoppingCart, User, LogOut, LayoutDashboard, Package, ArrowLeftRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import AnnouncementBell from "@/components/Announcements";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header({ brand }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const loc = useLocation();
  const { brand: brandKey } = useParams();

  const dark = brand?.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";
  const subFg = dark ? "rgba(245,242,235,0.65)" : "#8C8581";
  const hoverBg = dark ? "rgba(255,255,255,0.06)" : "#F5F2EB";
  const primary = brand?.primary || "#C85A32";
  const headerBg = dark ? "rgba(15,20,17,0.85)" : "rgba(253,251,247,0.8)";
  const borderColor = dark ? "rgba(255,255,255,0.08)" : "rgba(45,38,34,0.1)";

  const base = brandKey ? `/b/${brandKey}` : "";

  const navLink = (to, label, testId) => (
    <Link
      to={to}
      data-testid={testId}
      className="text-sm font-medium transition-colors"
      style={{ color: loc.pathname === to ? primary : fg }}
    >
      {label}
    </Link>
  );

  // Admin context (no brand) – simpler header
  if (!brand) {
    return (
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-6">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
            <img
              src="/brand-logos/bigbillion.jpg"
              alt="Big Billion Food and Beverages Pvt Ltd"
              data-testid="parent-logo-admin"
              className="h-10 w-auto object-contain rounded-md"
            />
            <span className="text-[10px] tracking-widest uppercase text-[#8C8581] hidden sm:inline">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <Button data-testid="menu-logout" variant="outline" size="sm" onClick={() => { logout(); nav("/"); }}>
                <LogOut className="w-4 h-4 mr-1" /> Log out
              </Button>
            )}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className="sticky top-0 z-50"
      style={{ backdropFilter: "blur(20px)", backgroundColor: headerBg, borderBottom: `1px solid ${borderColor}` }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-6">
        <Link to={base} data-testid="brand-logo" className="flex items-center gap-3">
          <div className="rounded-lg px-2.5 py-1.5 shadow-sm flex items-center justify-center"
            style={{ background: brand.logo_bg || "#FFFFFF" }}>
            <img
              src={brand.logo_url}
              alt={`${brand.name} logo`}
              data-testid={`header-logo-${brand.key}`}
              className="h-8 w-auto object-contain"
            />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-bold text-sm tracking-tight" style={{ color: fg }}>{brand.name}</span>
            <span className="text-[10px] tracking-widest uppercase" style={{ color: subFg }}>A Big Billion brand</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLink(base, "Home", "nav-home")}
          {navLink(`${base}/shop`, "Shop", "nav-shop")}
          {user && navLink(`${base}/orders`, "My Orders", "nav-orders")}
          <Link to="/" data-testid="switch-brand-link" className="text-sm font-medium opacity-70 hover:opacity-100 flex items-center gap-1" style={{ color: fg }}>
            <ArrowLeftRight className="w-3.5 h-3.5" /> Switch brand
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <AnnouncementBell brand={brand} />
          <Link to={`${base}/cart`} data-testid="cart-link" className="relative p-2 rounded-md transition-colors" style={{ color: fg }}>
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: primary }}>
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="outline" size="sm" className="gap-2" style={{ background: hoverBg, borderColor, color: fg }}>
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline truncate max-w-[140px]">
                    {user.store_code ? user.store_code : user.name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <div className="px-2 py-2 text-xs">
                  <p className="font-semibold text-[#2D2622] truncate">{user.store_name || user.name}</p>
                  {user.store_code && (
                    <p data-testid="header-store-code" className="font-mono font-bold tracking-wider mt-0.5" style={{ color: primary }}>{user.store_code}</p>
                  )}
                  <p className="text-[#8C8581] truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-orders" onClick={() => nav(`${base}/orders`)}>
                  <Package className="w-4 h-4 mr-2" /> My Orders
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav(base); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="login-btn" variant="ghost" size="sm" onClick={() => nav(`${base}/login`)} style={{ color: fg }}>
                Sign in
              </Button>
              <Button data-testid="signup-btn" size="sm" className="text-white" style={{ background: primary }} onClick={() => nav(`${base}/signup`)}>
                Get started
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
